<template>
  <div id="freebets-campaign-generator-view" :class="`min-h-screen px-4 md:!px-1.5 py-4 transition-all duration-300 ${isMenuOpen ? 'md:ml-64' : 'md:ml-20'
    }`" style="background: #f9fafb">
    <div class="max-w-full mx-auto">
      <div class="mb-4">
        <div class="flex flex-col gap-1">
          <h1 class="text-3xl font-extrabold text-slate-800 tracking-tight">
            {{ $route.params.id ? "Update" : "Create" }} Freebet
            <span v-if="$route.params.id" class="text-emerald-600">#{{ $route.params.id }}</span>
          </h1>
          <p class="text-slate-500 font-medium">
            Configure your freebet campaign settings
          </p>
        </div>
      </div>

      <div class="space-y-6">
        <section
          class="bg-white rounded-3xl p-4 shadow-sm border border-slate-100"
        >
          <div class="flex items-center gap-3 mb-3">
          
              <i class="fa-solid fa-gears text-gray-400 w-10 h-10 bg-gray-300/50 rounded-xl flex items-center justify-center"></i>
            
            <h2 class="font-semibold text-gray-800">
              General Settings ({{ formData.skin_id }})
            </h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div class="md:col-span-2 space-y-1">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">{{
                $t("campingGeneratorFreebet.title") }}*</label>
              <p class="text-xs text-slate-400">
                {{ $t("campingGeneratorFreebet.internalTitle") }}
              </p>
              <input v-model="formData.title" type="text" class="custom-input"
                :class="{ 'border-red-500 bg-red-50': validationErrors.title }" placeholder="Title" />
            </div>

            <div class="md:col-span-2 space-y-1">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">{{
                $t("campingGeneratorFreebet.description") }}*</label>
              <p class="text-xs text-slate-400">
                {{ $t("campingGeneratorFreebet.internalDescription") }}
              </p>
              <textarea v-model="formData.description" rows="3" class="custom-input resize-none" :class="{
                'border-red-500 bg-red-50': validationErrors.description,
              }" placeholder="Description"></textarea>
            </div>

            <div class="space-y-1">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">{{
                $t("campingGeneratorFreebet.note") }}*</label>
              <p class="text-xs text-slate-400">
                {{ $t("campingGeneratorFreebet.freebetNote") }}
              </p>
              <input v-model="formData.note" type="text" class="custom-input"
                :class="{ 'border-red-500 bg-red-50': validationErrors.note }" placeholder="Note" />
            </div>
          </div>
        </section>

        <div class="grid grid-cols-1 gap-3">
          <section class="bg-white rounded-3xl p-4 shadow-sm border border-slate-100">
            <div class="flex items-center gap-1.5 mb-2.5">
              <div
                class="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center"
              >
                <i class="fa-solid fa-bolt text-yellow-500 w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center"></i>
              </div>
              <h2 class="font-semibold text-gray-800">
                Configuration
              </h2>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div class="space-y-1">
                <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">{{
                  $t("campingGeneratorFreebet.quantityPerPlayer") }}*</label>
                <input v-model.number="formData.quantity" type="number" class="custom-input font-medium" :class="{
                  'border-red-500 bg-red-50': validationErrors.quantity,
                }" />
              </div>

              <div class="space-y-1">
                <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">{{
                  $t("campingGeneratorFreebet.maxPlayers") }}*</label>
                <input v-model.number="formData.max_players" type="number" class="custom-input font-medium" :class="{
                  'border-red-500 bg-red-50': validationErrors.max_players,
                }" />
              </div>

              <div class="space-y-1">
                <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">Start At*</label>
                <flat-pickr v-model="formData.start_at" :config="dateConfig" class="custom-input font-medium" />
              </div>
              <div class="space-y-1">
                <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">End At*</label>
                <flat-pickr v-model="formData.end_at" :config="dateConfig" class="custom-input font-medium" />
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div class="space-y-1 relative" v-click-outside="() => closeDropdown('game')">
                <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider"> 
                  Choose Games*</label>
                  <p class="text-xs text-slate-400">
                   Select the games for which you want to activate free bets. If you don't select any games, you can activate them across the entire provider
                 </p>
                <div class="relative">
                  <div @click="toggleDropdown('game')"
                    class="custom-input flex items-center justify-between cursor-pointer bg-white min-h-[42px] flex-wrap gap-2 p-2"
                    :class="{
                      'border-emerald-500 ring-2 ring-emerald-500/10': activeDropdown === 'game',
                      'border-red-500': validationErrors.game_id,
                    }">
                    <div v-if="formData.games && formData.games.length > 0" class="flex flex-wrap gap-2">
                      <span v-for="id in formData.games" :key="id"
                        class="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md text-sm font-medium border border-emerald-200">
                        {{ gameChoose[id] }}
                        <button type="button" @click.stop="removeGame(id)"
                          class="hover:text-emerald-900 ml-1 transition-colors">
                          <i class="fa-solid fa-xmark text-xs"></i>
                        </button>
                      </span>
                    </div>

                    <span v-else class="text-slate-400 italic">Select games...</span>

                    <i class="fa-solid fa-chevron-down text-slate-300 transition-transform ml-auto"
                      :class="{ 'rotate-180': activeDropdown === 'game' }"></i>
                  </div>
                  <div v-if="activeDropdown === 'game'"
                    class="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-150">
                    <div class="p-2 border-b border-slate-100 bg-slate-50">
                      <input v-model="searchQueries.game" type="text"
                        class="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-emerald-500"
                        placeholder="Type to filter games..." ref="gameSearchInput" />
                    </div>
                    <ul class="max-h-60 overflow-y-auto">
                      <li v-for="(label, value) in filteredGames" :key="value" @click="selectGame(value)"
                        class="px-4 py-2.5 hover:bg-emerald-50 cursor-pointer text-sm font-semibold flex items-center justify-between transition-colors"
                        :class="{
                          'bg-emerald-50 text-emerald-700': formData.games && formData.games.includes(value),
                          'text-slate-700': !formData.games || !formData.games.includes(value)
                        }">
                        {{ label }}
                        <i v-if="formData.games && formData.games.includes(value)"
                          class="fa-solid fa-check text-emerald-500"></i>
                      </li>
                      <li v-if="Object.keys(filteredGames).length === 0" class="p-4 text-center text-slate-400 text-xs">
                        No games found
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="justify-between p-0 justify-between flex items-start gap-1 flex-col">
                <span class="text-sm font-semibold text-slate-700 uppercase tracking-wider">Enable</span>
                <div class="relative inline-flex items-center cursor-pointer"
                  @click="formData.is_enabled = !formData.is_enabled">
                  <div class="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center px-1"
                    :class="formData.is_enabled ? 'bg-emerald-500' : 'bg-slate-300'
                      ">
                    <div class="bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200"
                      :class="formData.is_enabled ? 'translate-x-6' : 'translate-x-0'
                        "></div>
                  </div>
                  <span class="ml-2 text-xs font-semibold" :class="formData.is_enabled
                    ? 'text-emerald-600'
                    : 'text-slate-400'
                    ">
                    {{ formData.is_enabled ? "ACTIVE" : "INACTIVE" }}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section class="bg-white rounded-3xl p-4 shadow-sm border border-slate-100">
          <div class="flex items-center justify-between mb-2.5 flex-wrap">
            <div class="flex items-center gap-1.5 min-w-full md:min-w-fit">
              <div class="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <i class="fa-solid fa-dollar-sign text-amber-600"></i>
              </div>
              <h2 class="font-semibold text-gray-800">
                Currency Settings
              </h2>
            </div>
            <button @click="changeCurrency('add')"
              class="flex ms-auto mt-2 md:mt-0 items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl font-semibold text-sm hover:bg-emerald-100 transition-all">
              <i class="fa-solid fa-plus"></i> Add Currency
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            <div v-for="(curr, index) in formData.freebetcurrencies" :key="index"
              class="relative py-2 px-3 bg-slate-50 rounded-2xl border border-slate-200 group transition-all hover:border-emerald-200 hover:bg-white">
              <button v-if="formData.freebetcurrencies.length > 1" @click="changeCurrency('remove', index)"
                class="absolute -top-2 -right-2 w-7 h-7 bg-white border border-rose-200 text-rose-500 rounded-full shadow-sm hover:bg-rose-500 transition-all flex items-center justify-center z-10">
                <i class="fa-solid fa-times text-xs"></i>
              </button>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-1.5 md:gap-4">
                <div class="space-y-1 relative" v-click-outside="() => closeDropdown('curr' + index)">
                  <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Currency</label>
                  <div @click="toggleDropdown('curr' + index)"
                    class="w-full px-3 py-2 font-semibold rounded-lg border border-slate-200 bg-white flex items-center justify-between text-sm font-semibold cursor-pointer">
                    <span class="text-slate-600">{{ curr.currency_code }}</span>
                    <i class="fa-solid fa-angle-down text-slate-300"></i>
                  </div>

                  <div v-if="activeDropdown === 'curr' + index"
                    class="absolute z-[90] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                    <div class="p-2 border-b border-slate-100 bg-slate-50">
                      <input v-model="searchQueries.currency" type="text"
                        class="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md outline-none focus:border-emerald-500"
                        placeholder="Filter..." />
                    </div>
                    <ul class="max-h-40 overflow-y-auto">
                      <li v-for="opt in filteredCurrencies" :key="opt.code" @click="selectCurrency(index, opt.code)"
                        class="px-3 py-2 hover:bg-slate-50 cursor-pointer flex items-center justify-between text-xs"
                        :title="`${opt.code} (${opt.name})`">
                        <span
                          class="font-semibold block text-slate-700 flex-1 max-w-[50%] pr-3 overflow-hidden truncate">{{
                            opt.code }}</span>
                        <span
                          class="text-slate-400 block flex-1 max-w-[50%] text-end overflow-hidden truncate justify-end">{{
                            opt.name }}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="space-y-1">
                  <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Amount</label>
                  <input v-model="curr.amount" type="text" class="custom-input py-2 text-sm font-semibold"
                    @input="handleNumericInput($event, index, 'amount')" />
                </div>
                <div class="space-y-1">
                  <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Min Balance</label>
                  <input v-model="curr.min_balance" type="text" class="custom-input py-2 text-sm font-semibold"
                    @input="handleNumericInput($event, index, 'min_balance')" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="flex items-stretch justify-end pt-6 border-t border-slate-200 gap-1.5">
          <button @click="goBack"
            class="px-6 py-2.5 h-auto w-fit rounded-xl bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300 transition-all active:scale-95">
            {{ $t("util.back") }}
          </button>
          <button @click="handleSubmit"
            class="text-teal-900 bg-teal-100 inline-flex items-center gap-2 rounded-xl px-4 py-2 transition-colors duration-150 active:scale-95 border border-teal-200">
            <span class="text-lg">{{
              $route.params.id ? "Update" : "Create"
            }}</span>
            <i class="fa-solid fa-gift"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { format } from "date-fns";
import { mapGetters } from "vuex";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { postFetch, getFetch, putFetch } from "@/api/axiosClient";
import store from "@/store/index";

const clickOutside = {
  beforeMount(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted(el) {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};

export default {
  name: "FreeBetCampaignGenerator",
  components: { FlatPickr },
  directives: { "click-outside": clickOutside },
  data() {
    return {
      activeDropdown: null,
      searchQueries: { game: "", currency: "" },
      validationErrors: {},
      dateConfig: {
        enableTime: true,
        dateFormat: "Y-m-d H:i:S",
        time_24hr: true,
        disableMobile: true,
      },
      formData: {
        title: "",
        description: "",
        note: "",
        type: "all",
        skin_id: null,
        quantity: 1,
        max_players: 1000,
        min_cashout: 1,
        start_at: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
        end_at: format(
          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          "yyyy-MM-dd'T'HH:mm:ss",
        ),
        freebetcurrencies: [
          {
            currency_code: "EUR",
            amount: 10,
            min_balance: 0,
            min_wager: 0,
          },
        ],
        is_enabled: true,
        games: [],
        boost: 1,
        mode: "freebets",
      },
      currenciesOpt: [],
      gameChoose: {},
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen", "allowedGamesFreebet"]),
    filteredGames() {
      const q = this.searchQueries.game.toLowerCase();
      if (!q) return this.gameChoose;
      return Object.fromEntries(
        Object.entries(this.gameChoose).filter(([_, label]) =>
          label.toLowerCase().includes(q),
        ),
      );
    },
    filteredCurrencies() {
      const q = (this.searchQueries.currency ?? "").toLowerCase();
      const selectedCodes = this.formData?.freebetcurrencies.map(
        (c) => c?.currency_code,
      );
      let available = this.currenciesOpt.filter(
        (c) => c?.code && !selectedCodes.includes(c.code),
      );
      if (!q) return available;
      return available.filter(
        (c) =>
          c?.code?.toLowerCase().includes(q) ||
          c?.name?.toLowerCase().includes(q),
      );
    },
  },
  methods: {
    toggleDropdown(key) {
      if (this.activeDropdown === key) {
        this.activeDropdown = null;
      } else {
        this.activeDropdown = key;
        if (key === "game") {
          this.searchQueries.game = "";
          this.$nextTick(() => this.$refs.gameSearchInput?.focus());
        } else {
          this.searchQueries.currency = "";
        }
      }
    },
    closeDropdown(key) {
      if (this.activeDropdown === key) this.activeDropdown = null;
    },
    selectGame(id) {
      if (!Array.isArray(this.formData.games)) {
        this.formData.games = [];
      }
      if (!this.formData.games.includes(id)) {
        this.formData.games.push(id);
      } else {
        this.removeGame(id);
      }
    },
    removeGame(id) {
      this.formData.games = this.formData.games.filter(gameId => gameId !== id);
    },
    selectCurrency(index, code) {
      this.formData.freebetcurrencies[index].currency_code = code;
      this.activeDropdown = null;
    },
    async fetchCurrencySuggestions() {
      try {
        this.currenciesOpt = await getFetch(`currencies`, { query: "" });
      } catch (e) {
        this.currenciesOpt = [];
      }
    },
    changeCurrency(type, index = null) {
      if (type === "add") {
        const usedCodes = this.formData.freebetcurrencies.map(
          (c) => c.currency_code,
        );

        const firstAvailable = this.currenciesOpt.find(
          (c) => !usedCodes.includes(c.code),
        );

        if (!firstAvailable) {
          return Swal.fire({
            title: "Limit Reached",
            text: "All available currencies have already been added.",
            icon: "info",
          });
        }

        this.formData.freebetcurrencies.push({
          currency_code: firstAvailable.code,
          amount: 10,
          min_balance: 0,
          min_wager: 0,
        });
      } else if (
        type === "remove" &&
        index !== null &&
        this.formData.freebetcurrencies.length > 1
      ) {
        this.formData.freebetcurrencies.splice(index, 1);
      }
    },
    handleNumericInput(e, index, field) {
      this.formData.freebetcurrencies[index][field] = e.target.value.replace(
        /[^0-9.]/g,
        "",
      );
    },
    goBack() {
      const lang = this.$route.params.lang || "en";
      const skin = this.$route.params.skinFilt || this.$store.getters.skinStorage;
      this.$router.push({
        name: "Freebets",
        params: { lang, ...(skin ? { skinFilt: skin.toString() } : {}) },
      });
    },
    async handleSubmit() {
      this.validationErrors = {};
      [
        "title",
        "description",
        "note",
        "quantity",
        "games",
        "max_players",
      ].forEach((f) => {
        if (!this.formData[f]) this.validationErrors[f] = true;
      });
      if (Object.keys(this.validationErrors).length > 0)
        return Swal.fire("Error", "Fill required fields", "error");
      try {
        const lang = this.$route.params.lang || "en";
        const skin = this.$route.params.skinFilt || this.$store.getters.skinStorage;
        if (this.$route.params.id) {
          await putFetch(`/freebets/${this.$route.params.id}`, this.formData);
          this.goBack();
        } else {
          const res = await postFetch(`freebets`, this.formData);
          this.$router.push({
            name: "Freebets",
            params: { lang, ...(skin ? { skinFilt: skin.toString() } : {}), id: res.id },
          });
        }
      } catch (e) {
        Swal.fire("Error", "Operation failed", "error");
      }
    },
    async fetchFreebet() {
      const id = this.$route.params.id;
      if (id) {
        const data = await getFetch(`freebets/${id}`);
        if (data) {
          console.log(data);
          this.formData = { ...this.formData, ...data };
          this.formData.start_at = format(
            new Date(data.start_at),
            "yyyy-MM-dd'T'HH:mm:ss",
          );
          this.formData.end_at = format(
            new Date(data.end_at),
            "yyyy-MM-dd'T'HH:mm:ss",
          );
        }
      }
    },
    async fetchGames() {
      try {
        const res = await getFetch(`/games`);
        const games = Array.isArray(res) ? res : res?.data || [];
        this.gameChoose = Object.fromEntries(
          games.filter((g) => g.is_enabled).map((g) => [g.game_id, g.name]),
        );

        if (!this.$route.params.id) {
          const gameIds = Object.keys(this.gameChoose);

        }
      } catch (e) {
        console.error("Fetch games error", e);
        this.gameChoose = {};
      }
    },
  },

  async mounted() {
    const skin = this.$route.params.skinFilt || this.$store.getters.skinStorage;
    if (skin && skin !== "all") {
      this.formData.skin_id = parseInt(skin) || skin;
    }
    await this.fetchFreebet();
    await this.fetchCurrencySuggestions();
    this.fetchGames();
    await store.dispatch("getGame", { endpoint: "", params: {} });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/components/createFreebets_Rain.scss";
@import "@/assets/styles/components/createFreebets_Campaign.scss";

.custom-input {
  @apply w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all outline-none text-black;
}

ul::-webkit-scrollbar {
  width: 4px;
}

ul::-webkit-scrollbar-track {
  background: transparent;
}

ul::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}

select,
input {
  color: black;
}
</style>
